import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NAV_LINKS, NavLink } from '../../../../shared/constants/nav-links';
import { LogoComponent } from '../../../../shared/components/logo/logo.component';
import { SessionStore } from '../../../../shared/stores/session.store';

const NAV_SVG_ICONS = [
  { name: 'customers', path: 'assets/icons/customers.svg' },
  { name: 'applications', path: 'assets/icons/applications.svg' },
  { name: 'transactions', path: 'assets/icons/transactions.svg' },
  { name: 'nav_collapse', path: 'assets/icons/nav-collapse.svg' },
  { name: 'nav_expand', path: 'assets/icons/nav-expand.svg' },
] as const;

@Component({
  selector: 'app-navigation',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, MatIconModule, LogoComponent],
  templateUrl: './navigation.html',
  styleUrl: './navigation.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Navigation {
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly sessionStore = inject(SessionStore);
  private readonly router = inject(Router);

  /** Sidebar state */
  initialState = input<boolean>();
  readonly expanded = signal<boolean>(this.initialState() || true);
  readonly sidenavToggled = output<boolean>();

  /** Link states */
  readonly expandedGroups = signal<Record<string, boolean>>({});
  readonly links = computed(() => {
    const session = this.sessionStore.currentSession();
    const permissions = session?.permissions ?? [];

    return filterByPermissions(NAV_LINKS, permissions);
  });

  /** Current URL the user is on */
  readonly currentUrl = computed(() => this.router.url.replace(/^\//, ''));

  constructor() {
    // Add custom SVGs to mat-icon registry
    for (const { name, path } of NAV_SVG_ICONS) {
      this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(path));
    }
  }

  /** Toggle the navbar between collapsed and expanded */
  toggleSidebar(): void {
    const next = !this.expanded();
    this.expanded.set(next);
    this.sidenavToggled.emit(next);
  }

  /** Toggle a nav group (collapsed or open) */
  toggleGroup(link: NavLink): void {
    if (!link.children?.length) return;

    if (!this.expanded()) this.toggleSidebar();

    this.expandedGroups.update((state) => ({
      ...state,
      [link.url]: !state[link.url],
    }));
  }

  /** Return true if link is or is in expanded group */
  isGroupExpanded(link: NavLink): boolean {
    if (!link.children?.length) return false;

    const state = this.expandedGroups();
    if (state[link.url] !== undefined) return state[link.url];

    return link.children.some((child) => this.isLinkActive(child));
  }

  /** Return true if link is current url (or is group who's child is the current url) */
  isLinkActive(link: NavLink): boolean {
    const url = this.currentUrl();
    if (url === link.url) return true;

    return link.children?.some((child) => this.isLinkActive(child)) ?? false;
  }
}

/** Helper method to filter available links by permission set */
function filterByPermissions(links: NavLink[], permissions: string[]): NavLink[] {
  if (!permissions.length) return [];

  return links
    .map((link) => {
      // Recursively handle children
      if (link.children?.length) {
        const children = filterByPermissions(link.children, permissions);
        if (!children.length) return null;

        return { ...link, children };
      }

      // Check leaf links
      for (const p of link.requiredPermissions) {
        if (!permissions.includes(p)) {
          return null;
        }
      }
      return link;
    })
    .filter((link): link is NavLink => link !== null);
}
